import { calculationFare } from "@lib/fares";

export default class Booking implements be.Booking {
  edit: boolean = false;
  error: string = "";
  flags: {} = {};
  itineraryId: string = "";
  contactInfo: be.ContactInfo = {};
  creditCards: be.TokenizedCreditCard[] = [];
  originalPayment: be.Payment = {};
  otherInfo: be.OtherInfo = {};
  passengerDetails: be.PassengerDetails[] = [];
  legs: be.Reservation[] = [];
  payment: be.Payment = {};
  purchaseMethod?: string;
  pudPersonId?: string;
  roundtrip?: boolean;
  savedCreditCard?: be.TokenizedCreditCard;
  serviceArea?: number;
  serviceAreaName?: string;
  totalOriginal: number = 0;

  constructor(booking?: Partial<be.Booking>) {
    if (booking) {
      this.contactInfo = booking.contactInfo || {};
      this.creditCards = booking.creditCards || [];
      this.edit = !!booking.edit;
      this.flags = booking.flags || {};
      this.originalPayment = booking.originalPayment || {};
      this.otherInfo = booking.otherInfo || {};
      this.passengerDetails = booking.passengerDetails || [];
      this.payment = booking.payment || {};
      this.purchaseMethod = booking.purchaseMethod;
      this.legs = booking.legs || [];
      this.pudPersonId = booking.pudPersonId;
      this.roundtrip = booking.roundtrip === undefined ? undefined : !!booking.roundtrip;
      this.itineraryId = booking.itineraryId || "";
      this.error = booking.error || "";
      this.savedCreditCard = booking.savedCreditCard;
      this.serviceArea = booking.serviceArea;
      this.serviceAreaName = booking.serviceAreaName;
      this.totalOriginal = booking.totalOriginal || 0;
    }
  }

  activeLegs() {
    if (this.legs.length == 0) return [];
    return this.roundtrip ? this.legs : [this.legs[0]];
  }

  // Returns true if there are legs with undefined fares.
  hasIncompleteTotal() {
    return this.activeLegs().some((leg) => leg.service && leg.service?.fareDisplay === undefined);
  }

  hasPointsPayment() {
    return this.legs.some((leg) =>
      leg.payments?.items?.some((item) => item.type == "frequentriderpoints"),
    );
  }

  newVariance() {
    return -Math.abs(this.previouslyPaid()) + this.totalForCalculations();
  }

  totalDue() {
    return this.legs.reduce(
      (acc, leg) =>
        acc +
        (leg.payments?.amountDue === undefined
          ? leg.service?.fareDisplay || 0
          : leg.payments?.amountDue),
      0,
    );
  }

  totalPointsDeductionsInMoney() {
    return this.legs.reduce((acc, leg) => acc + (leg.payments?.totalPaymentAmount || 0), 0) * -1;
  }

  total() {
    return this.legs.reduce((acc, leg) => acc + (leg.service?.fareDisplay || 0), 0);
  }

  /**
   * Total fare for calculations (variance, points, etc.)
   * Uses the repriced fareComponents total when fareDisplay is 0 (hidden fare scenario)
   */
  totalForCalculations() {
    return this.legs.reduce((acc, leg) => acc + calculationFare(leg), 0);
  }

  /**
   * Calculate the total original fare before discounts
   * For legs with discountDisplayMode === 1, use originalFare; otherwise use fareDisplay
   */
  totalBeforeDiscounts() {
    return this.activeLegs().reduce((acc, leg) => {
      if (leg.service?.discountDisplayMode === 1 && leg.service?.originalFare !== undefined) {
        return acc + leg.service.originalFare;
      }
      return acc + (leg.service?.fareDisplay || 0);
    }, 0);
  }

  /**
   * Returns true if any leg has a discount applied and should show strikethrough pricing
   * Checks both that discountDisplayMode is enabled AND that originalFare > fareDisplay
   */
  shouldShowDiscountStrikethrough() {
    return this.activeLegs().some(
      (leg) =>
        leg.service?.discountDisplayMode === 1 &&
        leg.service?.originalFare !== undefined &&
        leg.service.originalFare > (leg.service?.fareDisplay || 0),
    );
  }

  originalTotal() {
    return Number(this.totalOriginal || 0);
  }

  originalVariance() {
    return Number(this.payment?.variance || 0);
  }

  previouslyPaid() {
    return this.originalTotal() - this.originalVariance();
  }

  showGrossOnly() {
    return this.legs.some((l) => l.service?.fareDisplayMode == "gross_only");
  }
}
